/**
 * TrialsReviewPanel — admin queue for incoming trial requests.
 *
 * Visible when ui_flags.show_trials_review (founder + sales_manager +
 * sales_person, per the matrix). Actions Approve / Reject / Hold call
 * into /api/admin/trials/{id}/{action}.
 *
 * Auth0 magic-link provisioning is NOT wired here — the Approve action
 * records the decision; the actual user provisioning runs in Half B
 * (next iteration). This panel surfaces a "Pending Auth0 provisioning"
 * pill on approved-but-not-yet-magic-linked rows so the operator knows
 * what the system is waiting on.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw, CheckCircle2, X, PauseCircle, RotateCcw,
  AlertTriangle, Mail, MailCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUS_STYLES = {
  pending_review: "bg-amber-50 text-amber-800 border-amber-300",
  approved:       "bg-emerald-50 text-emerald-800 border-emerald-300",
  rejected:       "bg-rose-50 text-rose-700 border-rose-300",
  on_hold:        "bg-zinc-100 text-zinc-700 border-zinc-300",
  expired:        "bg-zinc-50 text-zinc-500 border-zinc-200 line-through",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.pending_review;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10.5px] tracking-[0.12em] uppercase border ${cls}`}
      data-testid={`trial-status-${status}`}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function ReasonModal({ title, label, ctaLabel, ctaColor, onConfirm, onClose, submitting }) {
  const [reason, setReason] = useState("");
  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center p-4"
      data-testid="trial-reason-modal"
    >
      <div className="bg-white max-w-lg w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h3 className="ek-heading text-lg">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-black"
            data-testid="trial-reason-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6">
          <label className="block text-[12px] tracking-[0.14em] uppercase text-zinc-500">
            {label}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              className="mt-2 w-full border border-zinc-300 px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal text-black"
              data-testid="trial-reason-textarea"
              placeholder="Optional context — surfaces in the audit log."
            />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-200 bg-zinc-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-[12px] tracking-[0.14em] uppercase text-zinc-700 hover:text-black"
            data-testid="trial-reason-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={submitting}
            className="ek-btn text-white text-[12px] tracking-[0.14em] uppercase px-5 py-2 disabled:opacity-60"
            style={{ backgroundColor: ctaColor }}
            data-testid="trial-reason-confirm"
          >
            {submitting ? "Submitting…" : ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function LiteSummary({ s }) {
  if (!s) {
    return <span className="text-[11.5px] text-zinc-400">No lite session linked</span>;
  }
  return (
    <div className="text-[11.5px] text-zinc-700">
      <div>
        <strong>{s.framework_id || "—"}</strong>
      </div>
      <div className="text-zinc-500">
        Overall {typeof s.overall === "number" ? s.overall.toFixed(1) : "—"}
        {" · "}
        {typeof s.completeness_pct === "number" ? `${Math.round(s.completeness_pct)}%` : "—"} complete
      </div>
    </div>
  );
}

function TrialRow({ trial, onAction }) {
  const decidable = trial.status === "pending_review" || trial.status === "on_hold";
  const reopenable = trial.status === "on_hold" || trial.status === "rejected";
  return (
    <tr className="border-t border-zinc-200 align-top" data-testid={`trial-row-${trial.id}`}>
      <td className="px-3 py-3 text-[12.5px]">
        <div className="font-medium">{trial.full_name}</div>
        <div className="text-zinc-500 inline-flex items-center gap-1">
          <Mail size={11} /> {trial.business_email}
          {trial.business_email_warning && (
            <span
              className="ml-1 inline-flex items-center text-[10px] tracking-[0.12em] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5"
              title="Free-mail domain — corroborate before approving"
              data-testid={`trial-row-free-email-${trial.id}`}
            >
              <AlertTriangle size={10} /> Personal
            </span>
          )}
        </div>
        <div className="text-zinc-500">{trial.job_title}</div>
      </td>
      <td className="px-3 py-3 text-[12.5px]">
        <div className="font-medium">{trial.company}</div>
        <div className="text-zinc-500">{trial.sector} · {trial.company_size_band}</div>
        <div className="text-zinc-500">{trial.country}</div>
      </td>
      <td className="px-3 py-3"><LiteSummary s={trial.lite_session_summary} /></td>
      <td className="px-3 py-3"><StatusPill status={trial.status} /></td>
      <td className="px-3 py-3 text-[11.5px] text-zinc-500">{fmtDate(trial.created_at)}</td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-2 justify-end">
          {decidable && (
            <>
              <button
                type="button"
                onClick={() => onAction(trial, "approve")}
                className="ek-btn bg-[#7737F6] text-white text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1"
                data-testid={`trial-approve-${trial.id}`}
              >
                <CheckCircle2 size={12} /> Approve
              </button>
              <button
                type="button"
                onClick={() => onAction(trial, "reject")}
                className="border border-rose-300 text-rose-700 text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:bg-rose-50"
                data-testid={`trial-reject-${trial.id}`}
              >
                <X size={12} /> Reject
              </button>
              {trial.status === "pending_review" && (
                <button
                  type="button"
                  onClick={() => onAction(trial, "hold")}
                  className="border border-zinc-300 text-zinc-700 text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:border-black"
                  data-testid={`trial-hold-${trial.id}`}
                >
                  <PauseCircle size={12} /> Hold
                </button>
              )}
            </>
          )}
          {reopenable && (
            <button
              type="button"
              onClick={() => onAction(trial, "reopen")}
              className="border border-zinc-300 text-zinc-700 text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:border-black"
              data-testid={`trial-reopen-${trial.id}`}
            >
              <RotateCcw size={12} /> Re-open
            </button>
          )}
          {trial.status === "approved" && !trial.magic_link_sent_at && (
            <span
              className="text-[10px] tracking-[0.12em] uppercase text-zinc-500 px-2 py-1.5 border border-dashed border-zinc-300"
              data-testid={`trial-pending-auth0-${trial.id}`}
              title="Approved — Auth0 magic-link provisioning still pending"
            >
              Pending Auth0 provisioning
            </span>
          )}
          {trial.status === "approved" && (
            <button
              type="button"
              onClick={() => onAction(trial, "resend")}
              className="border border-[#7737F6] text-[#7737F6] text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:bg-[#7737F6] hover:text-white"
              data-testid={`trial-resend-magic-link-${trial.id}`}
              title="Re-send the Auth0 passwordless magic-link"
            >
              <MailCheck size={12} /> Resend link
            </button>
          )}
        </div>
        {trial.review_decision_reason && (
          <div className="text-[11px] text-zinc-500 mt-2 max-w-xs italic">
            “{trial.review_decision_reason}”
          </div>
        )}
      </td>
    </tr>
  );
}

export default function TrialsReviewPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { trial, action }
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/admin/trials${statusFilter ? `?status_filter=${statusFilter}` : ""}`,
      );
      setItems(r.data?.items || []);
      setCounts(r.data?.counts_by_status || {});
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) toast.error("You don't have access to trial requests.");
      else toast.error("Couldn't load trial requests.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const upsert = (updated) => {
    setItems((prev) => {
      // If the new status no longer matches the filter, drop the row;
      // otherwise replace in place.
      const matches = !statusFilter || updated.status === statusFilter;
      const idx = prev.findIndex((d) => d.id === updated.id);
      if (idx === -1) return matches ? [updated, ...prev] : prev;
      if (!matches) return prev.filter((d) => d.id !== updated.id);
      const next = prev.slice();
      next[idx] = updated;
      return next;
    });
    // Refresh counts after every decision.
    api.get("/admin/trials").then((r) => {
      setCounts(r.data?.counts_by_status || {});
    }).catch(() => {});
  };

  const runAction = async (action, reason) => {
    if (!modal) return;
    const { trial } = modal;
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/trials/${trial.id}/${action}`, { reason });
      toast.success(
        action === "approve" ? "Approved." :
        action === "reject" ? "Rejected — polite decline sent." :
        action === "hold" ? "Put on hold." :
        "Re-opened.",
      );
      upsert(r.data);
      setModal(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail.message || detail.error) : detail;
      toast.error(msg || "Action failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const tableEmpty = !loading && items.length === 0;

  const handleAction = (trial, action) => {
    if (action === "reopen") {
      // No reason prompt for re-open — direct call.
      setModal({ trial, action });
      // Auto-confirm without modal.
      setSubmitting(true);
      api.post(`/admin/trials/${trial.id}/reopen`)
        .then((r) => {
          toast.success("Re-opened.");
          upsert(r.data);
          setModal(null);
        })
        .catch((err) => {
          const detail = err?.response?.data?.detail;
          toast.error(typeof detail === "object" ? (detail.message || detail.error) : (detail || "Re-open failed."));
        })
        .finally(() => setSubmitting(false));
      return;
    }
    if (action === "resend") {
      // No reason prompt for resend — direct call.
      setSubmitting(true);
      api.post(`/admin/trials/${trial.id}/resend-magic-link`)
        .then((r) => {
          const sent = r.data?.send_result?.sent;
          if (sent) toast.success(`Magic link re-sent to ${trial.business_email}`);
          else toast.error(`Resend failed: ${r.data?.send_result?.reason || "unknown"}`);
          upsert(r.data);
        })
        .catch((err) => {
          const detail = err?.response?.data?.detail;
          if (detail === "auth0_pwless_not_configured") {
            toast.error("Auth0 passwordless not configured.");
          } else {
            toast.error(typeof detail === "object" ? (detail.message || detail.error) : (detail || "Resend failed."));
          }
        })
        .finally(() => setSubmitting(false));
      return;
    }
    setModal({ trial, action });
  };

  const filterOptions = useMemo(() => [
    { v: "", label: "All statuses" },
    { v: "pending_review", label: "Pending review" },
    { v: "on_hold", label: "On hold" },
    { v: "approved", label: "Approved" },
    { v: "rejected", label: "Rejected" },
  ], []);

  return (
    <div data-testid="trials-review-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Trial requests</h2>
          <p className="text-[13px] text-zinc-500 mt-1 max-w-2xl">
            Vetting queue for visitors who asked to trial the full EKSELENS360 assessment.
            Approval here records the decision; the Auth0 magic-link provisioning ships in
            the next iteration.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-zinc-300 px-3 py-2 text-[12.5px]"
            data-testid="trials-status-filter"
          >
            {filterOptions.map((o) => (
              <option key={o.v} value={o.v}>
                {o.label}{counts[o.v] ? ` (${counts[o.v]})` : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={load}
            className="ek-btn ek-btn-outline text-[12px] inline-flex items-center gap-2"
            data-testid="trials-refresh"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-zinc-200">
        <table className="min-w-full text-[13px]">
          <thead className="bg-zinc-50 text-zinc-500 text-left text-[11px] tracking-[0.12em] uppercase">
            <tr>
              <th className="px-3 py-2">Person</th>
              <th className="px-3 py-2">Company</th>
              <th className="px-3 py-2">Lite session</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Submitted</th>
              <th className="px-3 py-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-zinc-500">Loading…</td>
              </tr>
            )}
            {tableEmpty && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center">
                  <div className="text-zinc-700 font-medium" data-testid="trials-empty-headline">
                    No trial requests in this view.
                  </div>
                  <div className="text-zinc-500 text-[12.5px] mt-1">
                    Submissions come from the public site after a visitor runs a lite assessment
                    and clicks "Unlock the full assessment".
                  </div>
                </td>
              </tr>
            )}
            {!loading &&
              items.map((t) => (
                <TrialRow key={t.id} trial={t} onAction={handleAction} />
              ))}
          </tbody>
        </table>
      </div>

      {modal && modal.action !== "reopen" && (
        <ReasonModal
          title={
            modal.action === "approve" ? `Approve ${modal.trial.full_name}` :
            modal.action === "reject" ? `Reject ${modal.trial.full_name}` :
            `Hold ${modal.trial.full_name}`
          }
          label={
            modal.action === "approve" ? "Reason for approval (internal note, optional)" :
            modal.action === "reject" ? "Reason for decline (sent in the decline email)" :
            "Reason to hold (internal note, optional)"
          }
          ctaLabel={
            modal.action === "approve" ? "Approve" :
            modal.action === "reject" ? "Reject + email decline" :
            "Hold"
          }
          ctaColor={
            modal.action === "approve" ? "#7737F6" :
            modal.action === "reject" ? "#e11d48" :
            "#3f3f46"
          }
          submitting={submitting}
          onClose={() => setModal(null)}
          onConfirm={(reason) => runAction(modal.action, reason)}
        />
      )}
    </div>
  );
}
